import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  BarChart, 
  Calendar, 
  ChevronDown, 
  Download, 
  Mail, 
  Plus, 
  RefreshCw 
} from "lucide-react";
import { CampaignAnalyticsDashboard } from "@/components/communications/CampaignAnalyticsDashboard";
import { 
  Breadcrumb, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbList, 
  BreadcrumbPage, 
  BreadcrumbSeparator 
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";

export default function CampaignAnalytics() {
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
    to: new Date()
  });
  
  const handleExport = () => {
    // Handle exporting analytics data
    alert("Analytics data would be exported here");
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-1">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/communications">
                Communications
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Campaign Analytics</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Campaign Analytics</h1>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  New Campaign
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Mail className="mr-2 h-4 w-4" />
                  Email Campaign
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Calendar className="mr-2 h-4 w-4" />
                  Schedule Campaign
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      <Tabs defaultValue="overview" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
            <TabsTrigger value="contacts">Contact Engagement</TabsTrigger>
            <TabsTrigger value="reports">Custom Reports</TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <DatePickerWithRange date={date} setDate={setDate} />
            <Button variant="outline" size="icon">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <TabsContent value="overview">
          <CampaignAnalyticsDashboard initialPeriod="month" />
        </TabsContent>
        
        <TabsContent value="campaigns">
          <div className="flex h-[400px] items-center justify-center border rounded-md">
            <div className="flex flex-col items-center text-center">
              <BarChart className="h-10 w-10 text-muted-foreground mb-2" />
              <h3 className="text-lg font-medium">Detailed Campaign Analysis</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Compare performance across campaigns, analyze trends, and discover insights to improve your future campaigns.
              </p>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="contacts">
          <div className="flex h-[400px] items-center justify-center border rounded-md">
            <div className="flex flex-col items-center text-center">
              <Mail className="h-10 w-10 text-muted-foreground mb-2" />
              <h3 className="text-lg font-medium">Contact Engagement Analysis</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Track how individual contacts interact with your campaigns and identify your most engaged contacts.
              </p>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="reports">
          <div className="flex h-[400px] items-center justify-center border rounded-md">
            <div className="flex flex-col items-center text-center">
              <BarChart className="h-10 w-10 text-muted-foreground mb-2" />
              <h3 className="text-lg font-medium">Custom Reports</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Create custom reports to visualize specific metrics and share insights with your team.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}